import { Col } from "src/components/layout";
import FormLayout from "src/layouts/FormLayout";
import { useState } from "react";
import { useRedirectToPage } from "src/hooks";
import { useDlaFormSubmit } from "src/hooks/useDlaFormSubmit";
import { useFormLoad } from "src/hooks/useFormLoad";
import { useWebClientApi } from "src/api";
import { useAuth } from "src/context";
import {
  fromExpensesList,
  type ExpenseFormData,
  type ExpenseListFormData,
} from "src/api/adapters/expense";
import { ButtonPopup, ButtonPopupItem } from "src/components/ButtonPopup";
import { ExpenseOptions } from "src/constants";
import AlimonyModal from "./AlimonyModal";
import ChildSupportModal from "./ChildSupportModal";
import GarnishmentModal from "./GarnishmentModal";
import ExpenseItem from "./ExpenseItem/ExpenseItem";
import OtherExpenseModal from "./OtherExpenseModal";
import {
  Add01Icon,
  FormTable,
  FormTableBody,
  MoneySecurityIcon,
} from "@porchpass/ui";
import { SkipSection } from "@porchpass/api-client";

export default () => {
  const api = useWebClientApi();
  const { user: firebaseUser } = useAuth();
  const [focusedExpense, setFocusedExpense] =
    useState<Partial<ExpenseFormData>>();

  const { formData, reloadFormData } = useFormLoad<ExpenseListFormData>(
    { expenses: [] },
    () => api.getExpenses(),
    (data) => fromExpensesList(data as Parameters<typeof fromExpensesList>[0]),
    { enabled: !!firebaseUser },
  );

  const closeHandler = () => {
    setFocusedExpense(undefined);
  };

  const confirmHandler = () => {
    setFocusedExpense(undefined);
    reloadFormData();
  };

  const [skipSectionSubmitHandler, skipLoading] = useDlaFormSubmit(async () => {
    await api.skipSection(SkipSection.Expenses);
  });

  const redirectToBankruptcyPage = useRedirectToPage("/expenses/bankruptcy");

  const clickSubmitHandler = async () => {
    if (formData.expenses.length == 0) {
      await skipSectionSubmitHandler({});
    }
    redirectToBankruptcyPage();
  };

  const otherExpensesCount = formData.expenses.filter(
    (expense) => expense.expense_type === "OTHER_SUPPORT",
  ).length;

  const updatedExpenseOptions = ExpenseOptions.filter(
    (option) =>
      (option.expense_type === "OTHER_SUPPORT" && otherExpensesCount < 2) ||
      !formData.expenses.find(
        (expense) => expense.expense_type === option.expense_type,
      ),
  );

  let footerTitle = "Next";
  if (formData.expenses.length === 0) footerTitle = "I don't pay any";
  return (
    <FormLayout
      title="Expenses"
      headerTitle="Expenses"
      subtitle={
        updatedExpenseOptions.length > 0
          ? "Please add any expenses you currently pay."
          : "Maximum of 1 of each expense type and 2 other expenses allowed."
      }
      stepIndex={6}
      footerTitle={footerTitle}
      clickHandler={() => void clickSubmitHandler()}
      isLoading={skipLoading}
    >
      <Col>
        <FormTable
          title="Expenses"
          headerIcon={
            <MoneySecurityIcon
              variant="bulk-rounded"
              size={16}
              className="text-[#017C3F]"
            />
          }
        >
          {updatedExpenseOptions.length > 0 && (
            <FormTableBody className="!p-4">
              <ButtonPopup
                icon={<Add01Icon variant="stroke-rounded" size={16} />}
                text="Add Expense"
              >
                {updatedExpenseOptions.map((option) => (
                  <ButtonPopupItem
                    key={option.expense_type}
                    title={option.title}
                    clickHandler={() =>
                      setFocusedExpense({ expense_type: option.expense_type })
                    }
                  />
                ))}
              </ButtonPopup>
            </FormTableBody>
          )}
          {formData.expenses &&
            formData.expenses.map((expense, index) => (
              <ExpenseItem
                key={expense.id}
                index={index}
                expense={expense}
                editHandler={() => setFocusedExpense(expense)}
                closeHandler={closeHandler}
                deleteHandler={confirmHandler}
              />
            ))}
        </FormTable>
      </Col>
      {focusedExpense && focusedExpense.expense_type === "ALIMONY" && (
        <AlimonyModal
          expense={focusedExpense}
          closeHandler={closeHandler}
          confirmHandler={confirmHandler}
        />
      )}
      {focusedExpense && focusedExpense.expense_type === "CHILD_SUPPORT" && (
        <ChildSupportModal
          expense={focusedExpense}
          closeHandler={closeHandler}
          confirmHandler={confirmHandler}
        />
      )}
      {focusedExpense && focusedExpense.expense_type === "GARNISHMENT" && (
        <GarnishmentModal
          expense={focusedExpense}
          closeHandler={closeHandler}
          confirmHandler={confirmHandler}
        />
      )}
      {focusedExpense && focusedExpense.expense_type === "OTHER_SUPPORT" && (
        <OtherExpenseModal
          expense={focusedExpense}
          closeHandler={closeHandler}
          confirmHandler={confirmHandler}
        />
      )}
    </FormLayout>
  );
};
